/* src/components/customGame/setup/panels/stimuli/ColorStimulusPanel.js */
import React from 'react';
import PropTypes from 'prop-types';

// Re-using the SubSettingsSection helper
const SubSettingsSection = ({ title, children, helpText = "" }) => (
    <div className="mb-4 pt-3">
        <label className="block text-sm font-semibold text-gray-700 mb-1">{title}</label>
        {helpText && <p className="text-xs text-gray-500 mb-2">{helpText}</p>}
        <div className="space-y-3">
            {children}
        </div>
    </div>
);

SubSettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
};

const PREDEFINED_PALETTES = {
    "Basic 8": ["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF", "#000000", "#FFFFFF"], // Red, Green, Blue, Yellow, Magenta, Cyan, Black, White
    "Vivid 6": ["#E6194B", "#3CB44B", "#FFE119", "#4363D8", "#F58231", "#911EB4"], // More distinct vivids
    "Pastel 5": ["#FABED4", "#A9D18E", "#FFFAC8", "#A2CFFE", "#FFD8B1"],
};


const ColorStimulusPanel = ({ config, onConfigChange, pathPrefix, globalStimulusConfig }) => {
    const currentConfig = config || {};

    const handleChange = (key, value) => {
        onConfigChange(`${pathPrefix}.${key}`, value);
    };

    const handlePaletteTypeChange = (newType) => {
        handleChange('PaletteType', newType);
        if (newType.startsWith("Predefined Set")) {
            // Extract palette name like "Basic 8" from "Predefined Set (Basic 8)"
            const match = newType.match(/\(([^)]+)\)/);
            if (match && match[1] && PREDEFINED_PALETTES[match[1]]) {
                handleChange('PaletteColors', PREDEFINED_PALETTES[match[1]]);
                handleChange('NumberOfColors', PREDEFINED_PALETTES[match[1]].length);
            }
        } else if (newType === "User Defined List") {
            // If switching to user-defined, initialize with a few defaults or keep existing
            if (!currentConfig.PaletteColors || currentConfig.PaletteColors.length === 0) {
                handleChange('PaletteColors', ["#FF0000", "#00FF00", "#0000FF"]);
            }
        }
    };

    // For User Defined List, manage colors as a comma-separated string for now
    // Will be replaced by a list of color pickers later.
    const handleUserDefinedColorsChange = (e) => {
        const colorString = e.target.value;
        const colorsArray = colorString.split(',')
            .map(color => color.trim())
            .filter(color => /^#([0-9A-Fa-f]{3}){1,2}$/.test(color)); // Basic hex validation
        handleChange('PaletteColorsUserString', colorString); // Store the raw string
        handleChange('PaletteColors', colorsArray); // Store the parsed array
    };
    
    const isTextColorOptionDisabled = () => {
        // Text Color option for StimulusShape is only relevant if a text-based stimulus is active
        const symbolicEnabled = globalStimulusConfig?.EnabledTypes?.includes('Symbolic');
        // Could also check for 'Audio' if SetType is 'Letters' or 'Words' and if it's rendered visually (not part of current plan)
        return !symbolicEnabled;
    };


    return (
        <div className="space-y-4">
            <SubSettingsSection title="Color Presentation" helpText="Define how and which colors are displayed.">
                <div>
                    <label htmlFor={`${pathPrefix}-StimulusShape`} className="block text-xs font-medium text-gray-600 mb-1">
                        Color Displayed As
                    </label>
                    <select
                        id={`${pathPrefix}-StimulusShape`}
                        name="StimulusShape"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.StimulusShape || 'Central Square'}
                        onChange={(e) => handleChange('StimulusShape', e.target.value)}
                    >
                        <option value="Central Square">Central Square/Shape</option>
                        <option value="Full Background">Full Screen Background</option>
                        <option value="Text Color" disabled={isTextColorOptionDisabled()}>
                            Color of Text (Requires a text-based stimulus like Symbolic)
                        </option>
                    </select>
                    {currentConfig.StimulusShape === 'Text Color' && isTextColorOptionDisabled() && (
                         <p className="mt-1 text-xs text-yellow-600 bg-yellow-50 p-2 rounded-md">
                            To use "Color of Text", please also enable a text-based stimulus modality (e.g., Symbolic: Letters/Numbers).
                        </p>
                    )}
                    {currentConfig.StimulusShape === 'Full Background' && (
                        <p className="mt-1 text-xs text-gray-500">
                            Ensure good contrast with any other on-screen elements if using full background color.
                        </p>
                    )}
                </div>
            </SubSettingsSection>

            <SubSettingsSection title="Color Palette" helpText="Define the set of colors used in the sequence.">
                <div>
                    <label htmlFor={`${pathPrefix}-PaletteType`} className="block text-xs font-medium text-gray-600 mb-1">
                        Palette Source
                    </label>
                    <select
                        id={`${pathPrefix}-PaletteType`}
                        name="PaletteType"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.PaletteType || 'Predefined Set (Basic 8)'}
                        onChange={(e) => handlePaletteTypeChange(e.target.value)}
                    >
                        {Object.keys(PREDEFINED_PALETTES).map(name => (
                             <option key={name} value={`Predefined Set (${name})`}>{`Predefined Set (${name})`}</option>
                        ))}
                        <option value="User Defined List">User Defined List</option>
                        {/* <option value="Generated">Generated (Based on Similarity)</option> */}
                    </select>
                </div>

                {currentConfig.PaletteType === 'User Defined List' && (
                    <div>
                        <label htmlFor={`${pathPrefix}-PaletteColorsUserString`} className="block text-xs font-medium text-gray-600 mb-1">
                            Custom Colors (comma-separated HEX, e.g., #FF0000,#00FF00)
                        </label>
                        <textarea
                            id={`${pathPrefix}-PaletteColorsUserString`}
                            name="PaletteColorsUserString"
                            rows="2"
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={currentConfig.PaletteColorsUserString || (Array.isArray(currentConfig.PaletteColors) ? currentConfig.PaletteColors.join(',') : '')}
                            onChange={handleUserDefinedColorsChange}
                            placeholder="#FF0000,#00FF00,#0000FF"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            A proper color picker interface will be added later. For now, use HEX codes.
                            {currentConfig.PaletteColors && ` Currently ${currentConfig.PaletteColors.length} valid colors defined.`}
                        </p>
                    </div>
                )}

                {(currentConfig.PaletteType === 'Generated') && ( // Placeholder for Generated
                    <div>
                        <label htmlFor={`${pathPrefix}-NumberOfColors`} className="block text-xs font-medium text-gray-600 mb-1">
                            Number of Colors to Generate
                        </label>
                        <input
                            type="number"
                            id={`${pathPrefix}-NumberOfColors`}
                            name="NumberOfColors"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={currentConfig.NumberOfColors || 8}
                            onChange={(e) => handleChange('NumberOfColors', parseInt(e.target.value, 10))}
                            min="3"
                            max="20"
                        />
                        {/* SimilarityMetric and MinSimilarityDistance inputs would go here for Generated mode */}
                        <p className="mt-1 text-xs text-gray-500">Similarity-based generation settings will be added later.</p>
                    </div>
                )}

                {/* Display current palette colors */}
                {Array.isArray(currentConfig.PaletteColors) && currentConfig.PaletteColors.length > 0 && (
                    <div className="mt-2">
                        <p className="block text-xs font-medium text-gray-600 mb-1">Current Palette Preview:</p>
                        <div className="flex flex-wrap gap-2">
                            {currentConfig.PaletteColors.map((color, index) => (
                                <div key={index} title={color} className="h-8 w-8 rounded border border-gray-400" style={{ backgroundColor: color }}></div>
                            ))}
                        </div>
                    </div>
                )}
            </SubSettingsSection>

            {/* Placeholder for Similarity settings for "Generated" palettes (Phase 2/3) */}
            {/* <SubSettingsSection title="Color Similarity (for Generated Palettes)" helpText="Control how distinct generated colors are.">
                 <p className="text-sm text-gray-500">Settings for Similarity Metric and Minimum Distance will appear here when 'Generated' palette source is selected and implemented.</p>
            </SubSettingsSection> */}

            {/* Debug section (optional) */}
            {/* <details className="mt-4 text-xs">
                <summary className="cursor-pointer text-gray-500">Show Color Panel Debug Info</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded text-gray-700 overflow-auto">
                    Path Prefix: {pathPrefix}{"\n"}
                    Config: {JSON.stringify(currentConfig, null, 2)}
                </pre>
            </details> */}
        </div>
    );
};

ColorStimulusPanel.propTypes = {
    config: PropTypes.shape({
        StimulusShape: PropTypes.string,
        PaletteType: PropTypes.string,
        PaletteColors: PropTypes.arrayOf(PropTypes.string),
        PaletteColorsUserString: PropTypes.string, // To store raw user input for comma-separated colors
        NumberOfColors: PropTypes.number,
        SimilarityMetric: PropTypes.string,
        MinSimilarityDistance: PropTypes.number,
    }),
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired, // e.g., "Stimulus.Color"
    globalStimulusConfig: PropTypes.object, // The entire Stimulus settings object
};

export default ColorStimulusPanel;